import React, {useState, useEffect, useRef} from 'react';
import AurStepper from '../../common/aur-stepper-tab/AurStepperTab';
import {Box, Typography} from '@mui/material';
import TeamMemberInfo from './tabs/TeamMemberInfo';
import AddClientInfo from './tabs/AddClientInfo';
import {t} from 'i18next';
import Review from './tabs/Review';
import AurPageHeader from '../../common/aur-page-header/AurPageHeader';
import {setShowModal} from '../../../redux/slices/unSaveChangesModalSlice';
import {resetUserRoles} from '../../../redux/slices/userRoleSlice';
import {useDispatch, useSelector} from 'react-redux';
import {
  setClientInfo,
  setProjectList,
  setTeamMember,
} from '../../../redux/slices/teamMemberSlice';
import PermissionGroupDrawer from '../../permission-groups/PermissionGroupDrawer';
import {getPermissionsList} from '../../../api/ClientManagementService';
import {setPermissions} from '../../../redux/slices/permissionsSlice';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {TeamMemberStatus} from './models';
import {RootState} from '../../../redux/store';
import {PERMISSION_GROUP_CATEGORY} from '../../../constants/constants';

const AddEditUser: React.FC = () => {
  const [isSysAdmin, setIsSysAdmin] = useState(false);
  const [clientId, setClientId] = useState<string>('');
  const steps = [
    {label: t('addEditTeamMember.steps.teamMemberInfo')},
    {label: t('addEditTeamMember.steps.clientInfo'), disabled: isSysAdmin},
    {label: t('addEditTeamMember.steps.review')},
    {label: t('addEditTeamMember.steps.teamMemberAdded')},
  ];

  const [activeStep, setActiveStep] = useState(0);
  const [timeStamp, setTimeStamp] = useState<string>('');
  const [showHCProjects, setShowHCProjects] = useState(false);
  const [permissionGroupOpen, setPermissionGroupOpen] = useState(false);
  const [category, setCategory] = useState<PERMISSION_GROUP_CATEGORY>(
    PERMISSION_GROUP_CATEGORY.CLIENT
  );
  const {analyticsInfo} = useSelector((state: RootState) => state.teamMember);

  const [clientBudgetConsumptionEnabled, setClientBudgetConsumptionEnabled] =
    useState<boolean>(false);
  const isSystemAdmin = useRef(false);

  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();

  useEffect(() => {
    fetchPermissions();
    dispatch(resetUserRoles());
  }, []);

  const fetchPermissions = async () => {
    try {
      const result = await getPermissionsList();
      dispatch(setPermissions(result?.data?.data));
    } catch {
      console.warn('Permission error');
    }
  };

  const showPermissionGroupsDrawer = (
    selectedClientId: string,
    category: PERMISSION_GROUP_CATEGORY
  ) => {
    setClientId(selectedClientId);
    setCategory(category);
    setPermissionGroupOpen(true);
  };

  const resetForm = () => {
    localStorage?.setItem('currentPath', '');
    dispatch(setShowModal(false));
    setActiveStep(0);
    dispatch(
      setTeamMember({
        firstName: '',
        lastName: '',
        email: '',
        isActive: false,
        isBillable: false,
        isLimitIp: false,
        ipAddressList: [],
        status: TeamMemberStatus.invited,
      })
    );
    dispatch(
      setClientInfo({
        role: '',
        client: '',
        permissionGroup: '',
        permissionGroupId: '',
        project: '',
        workspaces: [],
      })
    );
    dispatch(setProjectList([]));
    setIsSysAdmin(false);
  };

  const trackSteps = (
    index: number,
    name: string,
    action?: string,
    skipClick = false,
    sendMEmberInfo = false
  ) => {
    if (!skipClick) {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: name,
          type: GA_CLICK_TYPES.BUTTON,
          region: GA_REGIONS.TAB,
          action:
            action ||
            t('gaTracking.global.nextStepAction', {
              stepName: steps[activeStep].label,
            }),
        },
        ...(sendMEmberInfo && {
          member: {
            member_role: analyticsInfo.userRole,
            member_project_count: analyticsInfo.numberOfProject,
          },
        }),
        subPage: {
          subpage_name: steps[activeStep].label,
          subpage_type: GA_SUBPAGE_TYPE.TAB,
        },
      });
    }
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: {
        subpage_name: steps[index].label,
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });
  };

  return (
    <div className="aur-add-edit-user">
      <div className="header">
        <AurPageHeader
          heading={t('addEditTeamMember.headerTitle')}
          linkLabel={t('addEditTeamMember.headerLinkTitle')}
          path="/users"
          onBreadcrumbLinkClick={() => {
            trackGAEvent({
              eventName: GA_EVENT_NAMES.CLICK,
              click: {
                name: t('gaTracking.users.manageTeamMembers'),
                type: GA_CLICK_TYPES.LINK,
                region: GA_REGIONS.TAB,
                action: t('gaTracking.users.backToManageTeamMemberHomepage'),
              },
              subPage: {
                subpage_name: steps[activeStep].label,
                subpage_type: GA_SUBPAGE_TYPE.TAB,
              },
            });
          }}
        />

        <AurStepper
          activeStep={activeStep}
          steps={steps}
          setActiveStep={(step: number) => {
            setActiveStep(step);
            trackGAEvent({
              eventName: GA_EVENT_NAMES.CLICK,
              click: {
                name: steps[activeStep].label,
                type: GA_CLICK_TYPES.TAB,
                region: GA_REGIONS.BODY,
                action: t('gaTracking.global.previousStepAction', {
                  stepName: steps[step].label,
                }),
              },
              subPage: {
                subpage_name: steps[activeStep].label,
                subpage_type: GA_SUBPAGE_TYPE.TAB,
              },
            });
            trackGAEvent({
              eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
              subPage: {
                subpage_name: steps[step].label,
                subpage_type: GA_SUBPAGE_TYPE.TAB,
              },
            });
          }}
        />
      </div>

      <div
        className={`${activeStep >= 2 ? 'content' : 'content content-backgroundcolor'}`}
      >
        {activeStep == 0 ? (
          <Box
            id={`aur-stepper-panel-${activeStep}`}
            role="tabpanel"
            aria-labelledby={`${steps[activeStep].label}-${activeStep}`}
          >
            <Typography className="typo-text headline3_size title">
              {t('addEditTeamMember.formTitle')}
            </Typography>
            <TeamMemberInfo
              activeStep={activeStep}
              setActiveStep={(step: number) => {
                setActiveStep(step);
                trackSteps(step, t('gaTracking.users.saveAndContinue'));
              }}
              isSysAdmin={isSysAdmin}
              onSysAdmin={(isAdmin: boolean) => {
                isSystemAdmin.current = isAdmin;
                setIsSysAdmin(isAdmin);
              }}
            />
          </Box>
        ) : null}
        {activeStep == 1 ? (
          <Box
            id={`aur-stepper-panel-${activeStep}`}
            role="tabpanel"
            aria-labelledby={`${steps[activeStep].label}-${activeStep}`}
          >
            <AddClientInfo
              activeStep={activeStep}
              setActiveStep={(step: number) => {
                setActiveStep(step);
                trackSteps(step, t('gaTracking.users.saveAndContinue'));
              }}
              showHCProjects={showHCProjects}
              setShowHCProjects={setShowHCProjects}
              onCreateNewPermissionGroup={(
                clientId: string,
                category: PERMISSION_GROUP_CATEGORY
              ) => showPermissionGroupsDrawer(clientId, category)}
              onGetBudgetConsumptionEnabled={setClientBudgetConsumptionEnabled}
            />
          </Box>
        ) : null}
        {activeStep == 2 ? (
          <Box
            className="review-step"
            id={`aur-stepper-panel-${activeStep}`}
            role="tabpanel"
            aria-labelledby={`${steps[activeStep].label}-${activeStep}`}
          >
            <Review
              activeStep={activeStep}
              setActiveStep={(step: number) => {
                setActiveStep(step);
                step === 0 || step === 1
                  ? trackSteps(
                      step,
                      t('gaTracking.users.editDetails'),
                      t('gaTracking.global.previousStepAction', {
                        stepName: steps[step].label,
                      }),
                      true,
                      true
                    )
                  : trackSteps(
                      step,
                      t('gaTracking.users.everythingButton'),
                      undefined,
                      false,
                      true
                    );
              }}
              timeStamp={timeStamp}
              setTimeStamp={setTimeStamp}
              resetForm={resetForm}
            />
          </Box>
        ) : null}
        {activeStep == 3 ? (
          <Box
            className="review-step"
            id={`aur-stepper-panel-${activeStep}`}
            role="tabpanel"
            aria-labelledby={`${steps[activeStep].label}-${activeStep}`}
          >
            <Review
              activeStep={activeStep}
              setActiveStep={(step: number) => {
                setActiveStep(step);
                step === 0 || step === 1
                  ? trackSteps(
                      step,
                      t('gaTracking.users.editDetails'),
                      t('gaTracking.global.previousStepAction', {
                        stepName: steps[step].label,
                      }),
                      true,
                      true
                    )
                  : trackSteps(
                      step,
                      t('gaTracking.users.everythingButton'),
                      undefined,
                      false,
                      true
                    );
              }}
              timeStamp={timeStamp}
              setTimeStamp={setTimeStamp}
              resetForm={resetForm}
            />
          </Box>
        ) : null}
      </div>
      {permissionGroupOpen && (
        <PermissionGroupDrawer
          shouldShow={permissionGroupOpen}
          onClose={() => setPermissionGroupOpen(false)}
          clientId={clientId}
          budgetConsumptionEnabled={clientBudgetConsumptionEnabled}
          clientName={''}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onUpdate={() => {}}
          showAddGrpOnOpen={true}
          category={category}
        />
      )}
    </div>
  );
};

export default AddEditUser;
